/**
 * Hierarchical / heuristic sim defaults.
 *
 * Contract
 * --------
 * 1. BASE_* are the global living-exhibit defaults (Lab “reset”, empty preset).
 * 2. `resolveSimDefaults(partial, ctx)` merges BASE → heuristics → explicit keys.
 *    Only keys **not** in `ctx.explicit` may be filled by heuristics. User and
 *    preset authors always win on keys they set.
 * 3. Plugins / topology families can register heuristic sources later; v1 ships
 *    built-in sources (freeze, voxel, mutual gravity, large-n hash).
 *
 * Why this exists
 * ---------------
 * Freezing used to pin rechoose=0 and leave it stuck at 0 after unfreeze.
 * Voxel presets force freeze+rechoose0 (correct). Living demos should feel
 * alive — default rechoose is 25%, not a frozen graph.
 */

import type { SimParams } from "./params";

/** Global living mind-change rate (user-requested exhibit default). */
export const LIVING_RECHOOSE_PROB = 0.25;

/** Base bag before any topology / gravity heuristics. */
export const BASE_SIM_DEFAULTS: Readonly<
  Omit<
    SimParams,
    | "typeRules"
    | "relationWeights"
    | "wells"
    | "seed"
    | "relationProfile"
    | "model"
    | "initPositions"
    | "topology"
    | "rechooseMode"
    | "motionMode"
    | "gravityMode"
  > & {
    seed: number | null;
    model: string;
    initPositions: string;
    topology: string;
    rechooseMode: "independent" | "coupled";
    relationProfile: string;
    typeRules: null;
    relationWeights: null;
    motionMode: "kinematic" | "inertial";
    gravityMode: SimParams["gravityMode"];
    wells: [];
  }
> = {
  n: 1000,
  dim: 2,
  contraction: 0.995,
  friendStep: 0.02,
  enemyStep: 0.01,
  softening: 0.01,
  rechooseProb: LIVING_RECHOOSE_PROB,
  rechooseMode: "independent",
  rechooseCount: 1,
  initPositions: "uniform_box",
  topology: "random",
  ringStride: 1,
  forbidSelfLinks: true,
  freezeTopology: false,
  seed: 1,
  model: "friends_enemies",
  neighborRadius: 0.35,
  spatialHash: false,
  localTopology: false,
  gridExtent: 1.0,
  gridJitter: 0.02,
  relationProfile: "classic_fe",
  typeCount: 1,
  typeRules: null,
  relationWeights: null,
  motionMode: "kinematic",
  velocityDamping: 0.2,
  socialGain: 1.0,
  maxSpeed: 0.08,
  gravityMode: "none",
  gravityStrength: 0.001,
  gravitySoftening: 0.05,
  gravityRadius: 0,
  particleMass: 1.0,
  wells: [],
};

export type DefaultsContext = {
  /**
   * CamelCase keys the caller set explicitly (preset JSON / URL / slider).
   * Heuristics must not overwrite these.
   */
  explicit?: ReadonlySet<string>;
  /** Optional catalog tags (e.g. voxel) when resolving a preset. */
  tags?: readonly string[];
};

function isExplicit(ctx: DefaultsContext | undefined, key: string): boolean {
  return ctx?.explicit?.has(key) === true;
}

function isVoxelTopology(topology: string): boolean {
  return topology.startsWith("voxel_");
}

/**
 * Living rechoose rate for a topology family when the user has not pinned r.
 * Voxel lattices stay frozen (0). Everything else uses the global living default.
 */
export function livingRechooseFor(p: {
  topology?: string;
  freezeTopology?: boolean;
}): number {
  if (p.freezeTopology) return 0;
  if (p.topology && isVoxelTopology(p.topology)) return 0;
  return LIVING_RECHOOSE_PROB;
}

type HeuristicSource = {
  id: string;
  /** Apply in-place on draft; only write keys not in explicit. */
  apply: (draft: SimParams, ctx: DefaultsContext | undefined) => void;
};

/**
 * Ordered heuristic sources. Later sources may fill remaining unset keys.
 * Keep pure and cheap — this runs on every preset load / param merge.
 */
const HEURISTIC_SOURCES: readonly HeuristicSource[] = [
  {
    id: "voxel_lattice",
    apply(draft, ctx) {
      if (!isVoxelTopology(draft.topology)) return;
      // Voxel graphs are structural lattices — freeze unless user overrode.
      if (!isExplicit(ctx, "freezeTopology")) draft.freezeTopology = true;
      if (!isExplicit(ctx, "rechooseProb")) draft.rechooseProb = 0;
      if (!isExplicit(ctx, "initPositions") && draft.dim === 3) {
        // 3D voxels almost always want grid init; 2D voxel rare.
        draft.initPositions = "grid";
      }
    },
  },
  {
    id: "freeze_graph",
    apply(draft, ctx) {
      if (!draft.freezeTopology) return;
      if (!isExplicit(ctx, "rechooseProb")) draft.rechooseProb = 0;
    },
  },
  {
    id: "living_rechoose",
    apply(draft, ctx) {
      if (draft.freezeTopology) return;
      if (isVoxelTopology(draft.topology)) return;
      if (!isExplicit(ctx, "rechooseProb")) {
        draft.rechooseProb = livingRechooseFor(draft);
      }
    },
  },
  {
    id: "mutual_gravity_hash",
    apply(draft, ctx) {
      // Mutual gravity needs a neighborhood structure in the browser.
      if (draft.gravityMode !== "mutual") return;
      if (!isExplicit(ctx, "spatialHash")) draft.spatialHash = true;
    },
  },
  {
    id: "large_n_hash",
    apply(draft, ctx) {
      // Auto spatial hash for dense clouds when caller didn't decide.
      if (draft.n < 2500) return;
      if (!isExplicit(ctx, "spatialHash")) draft.spatialHash = true;
    },
  },
  {
    id: "reynolds_neighborhood",
    apply(draft, ctx) {
      if (draft.model !== "reynolds") return;
      if (!isExplicit(ctx, "spatialHash")) draft.spatialHash = true;
    },
  },
];

/**
 * Resolve a full SimParams bag from a partial + optional explicit-key set.
 * Does **not** validate ranges — call `validateSimParams` after.
 */
export function resolveSimDefaults(
  partial: Partial<SimParams> = {},
  ctx?: DefaultsContext,
): SimParams {
  const draft: SimParams = {
    ...(BASE_SIM_DEFAULTS as SimParams),
    ...partial,
    // Ensure arrays/objects aren't shared with BASE (override spread copies)
    wells: partial.wells ? partial.wells.map((w) => ({ ...w })) : [],
    typeRules: partial.typeRules
      ? {
          ...partial.typeRules,
          allow: partial.typeRules.allow
            ? Object.fromEntries(
                Object.entries(partial.typeRules.allow).map(([k, m]) => [
                  k,
                  m.map((row) => [...row]),
                ]),
              )
            : undefined,
        }
      : null,
    relationWeights: partial.relationWeights
      ? [...partial.relationWeights]
      : null,
  };

  // If caller didn't pass explicit set, treat every key present on partial as explicit.
  const explicit =
    ctx?.explicit ??
    new Set(
      Object.keys(partial).filter(
        (k) => (partial as Record<string, unknown>)[k] !== undefined,
      ),
    );
  const ctxResolved: DefaultsContext = { ...ctx, explicit };

  for (const src of HEURISTIC_SOURCES) {
    src.apply(draft, ctxResolved);
  }

  // Consistency: freeze always implies r=0 even if user set both inconsistently
  // via raw JSON — freeze wins on the rechoose side only when freeze is true.
  if (
    draft.freezeTopology &&
    draft.rechooseProb > 0 &&
    !isExplicit(ctxResolved, "rechooseProb")
  ) {
    draft.rechooseProb = 0;
  }

  return draft;
}

/** List of built-in heuristic source ids (for tests / docs). */
export function defaultHeuristicSourceIds(): string[] {
  return HEURISTIC_SOURCES.map((s) => s.id);
}
